import { useMemo } from "react";

import { isHighlighted } from "../lib/selection";
import type { Selection } from "../lib/selection";
import type { TopologyPayload } from "../lib/types/topology";
import type { ViewMode } from "../lib/viewMode";


interface ZonePanelProps {
  topology: TopologyPayload;
  selection?: Selection;
  onSelect?: (s: Selection | undefined) => void;
  viewMode: ViewMode;
}


/**
 * Zone chain cards, rendered as HTML in the right panel (formerly SVG cards
 * inside the canvas — moved out so the aircraft gets the full pane width).
 * eX3 shows NC/port zones in Data view; eX1 shows MCU chains in MCU view.
 */
export default function ZonePanel({
  topology, selection, onSelect, viewMode,
}: ZonePanelProps) {
  const isEx3 = topology.layout.topology.type === "nc-port";
  const visible = isEx3 ? viewMode === "data" : viewMode === "mcu";
  if (!visible) return null;

  return (
    <div className="mt-6">
      <h2 className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
        {isEx3 ? "Zone chain endpoints" : "MCU zone chains"}
      </h2>
      {isEx3 ? (
        <Ex3Zones topology={topology} selection={selection} onSelect={onSelect} />
      ) : (
        <Ex1McuChains topology={topology} selection={selection} onSelect={onSelect} />
      )}
    </div>
  );
}


function Ex3Zones({
  topology, selection, onSelect,
}: Omit<ZonePanelProps, "viewMode">) {
  return (
    <div className="flex flex-col gap-2">
      {topology.zones.map((z, i) => {
        const bright = isHighlighted(
          selection, { kind: "port", nc: z.nc, port: z.port }, topology,
        );
        return (
          <button
            key={`${z.nc}-${z.port}`}
            onClick={() => onSelect?.({ kind: "port", nc: z.nc, port: z.port })}
            className="rounded-lg bg-white p-3 text-left shadow-sm transition hover:shadow"
            style={{
              borderLeft: `4px solid ${z.color}`,
              opacity: bright ? 1 : 0.4,
            }}
          >
            <div className="text-[13px] font-bold text-slate-900">
              Zone {i + 1} · NC {z.nc} / Port {z.port}
            </div>
            <div className="text-[11px] text-slate-500">
              {z.cabin || "—"} · {z.seat_count} seats · {z.hps_count} HPS
            </div>
            {z.sib_start && (
              <div className="mt-1 font-mono text-[11px] text-slate-700">
                SIB {z.sib_start_seb}  {z.sib_start}
              </div>
            )}
            {z.sib_end && z.sib_end !== z.sib_start && (
              <div className="font-mono text-[11px] text-slate-700">
                SIB {z.sib_end_seb}  {z.sib_end}
              </div>
            )}
            <div className="mt-1 text-[10.5px] text-slate-400">
              Redundant → NC {z.redundant_nc} / P{z.redundant_port}
            </div>
          </button>
        );
      })}
    </div>
  );
}


function Ex1McuChains({
  topology, selection, onSelect,
}: Omit<ZonePanelProps, "viewMode">) {
  const { mcuList, portsByMcu, seatsByMcuPort } = useMemo(() => {
    const portsByMcu = new Map<number, Set<number>>();
    const seatsByMcuPort = new Map<string, number>();
    for (const s of Object.values(topology.seats)) {
      if (s.mcu === undefined || s.mcu === null) continue;
      let set = portsByMcu.get(s.mcu);
      if (!set) { set = new Set(); portsByMcu.set(s.mcu, set); }
      set.add(s.port);
      const key = `${s.mcu}-${s.port}`;
      seatsByMcuPort.set(key, (seatsByMcuPort.get(key) ?? 0) + 1);
    }
    const mcuList = [...portsByMcu.keys()].sort((a, b) => a - b);
    return { mcuList, portsByMcu, seatsByMcuPort };
  }, [topology.seats]);

  const colours = topology.layout.topology.zone_colors;

  return (
    <div className="flex flex-col gap-2">
      {mcuList.map((m) => {
        const ports = [...portsByMcu.get(m)!].sort((a, b) => a - b);
        const total = ports.reduce(
          (acc, p) => acc + (seatsByMcuPort.get(`${m}-${p}`) ?? 0), 0,
        );
        const headerColor = colours[`${m}-${ports[0]}`] ?? "#888";
        const bright = isHighlighted(selection, { kind: "nc", id: m }, topology);
        return (
          <div
            key={m}
            className="rounded-lg bg-white p-3 shadow-sm transition"
            style={{
              borderLeft: `4px solid ${headerColor}`,
              opacity: bright ? 1 : 0.4,
            }}
          >
            <button
              onClick={() => onSelect?.({ kind: "nc", id: m })}
              className="w-full text-left"
            >
              <div className="text-[13px] font-bold text-slate-900">
                MCU {m + 1} · {ports.length} port chain{ports.length !== 1 ? "s" : ""}
              </div>
              <div className="text-[11px] text-slate-500">{total} seats total</div>
            </button>
            <div className="mt-1.5 flex flex-col gap-0.5">
              {ports.map((p) => {
                const portBright = isHighlighted(
                  selection, { kind: "port", nc: m, port: p }, topology,
                );
                return (
                  <button
                    key={p}
                    onClick={() => onSelect?.({ kind: "port", nc: m, port: p })}
                    className="flex items-center gap-2 rounded px-1 py-0.5 text-left hover:bg-slate-50"
                    style={{ opacity: portBright ? 1 : 0.45 }}
                  >
                    <span
                      className="h-2.5 w-2.5 shrink-0 rounded-sm"
                      style={{ background: colours[`${m}-${p}`] ?? "#888" }}
                    />
                    <span className="text-[11px] text-slate-700">
                      Port {p} — {seatsByMcuPort.get(`${m}-${p}`) ?? 0} seats
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
